/**
 * Componente FeatureHelp - Ajuda Contextual Premium
 * 
 * Exibe um ícone clicável que abre um modal educativo explicando
 * funcionalidades do sistema com suporte a internacionalização.
 * 
 * @example
 * <FeatureHelp featureKey="observerMode" />
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, X, CheckCircle2, Lightbulb, BookOpen, Sparkles, Code2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

// Interface para estrutura de exemplo
interface HelpExample {
  scenario: string;
  description: string;
}

// Interface para variável documentada
interface HelpVariable {
  name: string;
  description: string;
}

// Interface para conteúdo de ajuda traduzido
interface HelpContent {
  title: string;
  subtitle: string;
  description: string;
  benefits: string[];
  variables?: HelpVariable[];
  examples: HelpExample[];
  tip: string;
  button: string;
}

// Props do componente
interface FeatureHelpProps {
  /** Chave da funcionalidade no namespace 'help' */
  featureKey: string;
  
  /** Tamanho do ícone */
  size?: 'sm' | 'md' | 'lg';
  
  /** Variante visual do ícone */
  variant?: 'default' | 'subtle' | 'primary';
  
  /** Classe CSS adicional */
  className?: string;
}

// Mapeamento de tamanhos do ícone
const sizeMap = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

// Mapeamento de variantes do ícone
const variantMap = {
  default: 'text-muted-foreground hover:text-foreground',
  subtle: 'text-muted-foreground/60 hover:text-muted-foreground',
  primary: 'text-primary/70 hover:text-primary',
};

export function FeatureHelp({
  featureKey,
  size = 'md',
  variant = 'default',
  className,
}: FeatureHelpProps) {
  const { t } = useTranslation('help');
  const [isOpen, setIsOpen] = useState(false);

  // Busca conteúdo traduzido
  const rawVariables = t(`${featureKey}.variables`, { returnObjects: true, defaultValue: '' });
  const content: HelpContent = {
    title: t(`${featureKey}.title`),
    subtitle: t(`${featureKey}.subtitle`),
    description: t(`${featureKey}.description`),
    benefits: t(`${featureKey}.benefits`, { returnObjects: true }) as string[],
    variables: Array.isArray(rawVariables) ? rawVariables as HelpVariable[] : undefined,
    examples: t(`${featureKey}.examples`, { returnObjects: true }) as HelpExample[],
    tip: t(`${featureKey}.tip`),
    button: t(`${featureKey}.button`),
  };

  // Labels das seções
  const sections = {
    whatIs: t('sections.whatIs'),
    benefits: t('sections.benefits'),
    variables: t('sections.variables', { defaultValue: 'Variáveis Disponíveis' }),
    examples: t('sections.examples'),
    tip: t('sections.tip'),
  };

  return (
    <>
      {/* Ícone de ajuda clicável */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={cn(
          'inline-flex items-center justify-center rounded-full',
          'transition-all duration-200 ease-out',
          'hover:scale-110 hover:bg-accent/50',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
          'p-1 -m-1',
          sizeMap[size],
          variantMap[variant],
          className
        )}
        aria-label={`Ajuda sobre ${content.title}`}
      >
        <HelpCircle className={cn(sizeMap[size])} />
      </button>

      {/* Modal educativo premium */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent 
          className="sm:max-w-lg max-h-[85vh] p-0 gap-0 overflow-hidden"
          preventCloseOnOutsideInteraction={false}
        >
          {/* Header com gradiente */}
          <DialogHeader className="relative px-6 pt-6 pb-4 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-b">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 p-2.5 rounded-xl bg-primary/10 text-primary">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-lg font-semibold text-foreground">
                  {content.title}
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {content.subtitle}
                </p>
              </div>
            </div>
          </DialogHeader>

          {/* Conteúdo com scroll */}
          <ScrollArea className="flex-1 max-h-[calc(85vh-180px)]">
            <div className="px-6 py-5 space-y-6">
              
              {/* Seção: O que é? */}
              <section className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <BookOpen className="h-4 w-4 text-primary" />
                  {sections.whatIs}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed pl-6">
                  {content.description}
                </p>
              </section>

              {/* Seção: Benefícios */}
              {Array.isArray(content.benefits) && content.benefits.length > 0 && (
                <section className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    {sections.benefits}
                  </div>
                  <ul className="space-y-2 pl-6">
                    {content.benefits.map((benefit, index) => (
                      <li 
                        key={index}
                        className="flex items-start gap-2.5 text-sm text-muted-foreground"
                      >
                        <span className="flex-shrink-0 mt-1.5 h-1.5 w-1.5 rounded-full bg-primary/60" />
                        <span className="leading-relaxed">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Seção: Variáveis Disponíveis (opcional) */}
              {Array.isArray(content.variables) && content.variables.length > 0 && (
                <section className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <Code2 className="h-4 w-4 text-violet-500" />
                    {sections.variables}
                  </div>
                  <div className="pl-6 space-y-1.5">
                    {content.variables.map((variable, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-2 text-sm"
                      >
                        <code className="flex-shrink-0 px-1.5 py-0.5 rounded bg-muted border border-border/50 text-xs font-mono text-foreground">
                          {`{{${variable.name}}}`}
                        </code>
                        <span className="text-muted-foreground leading-relaxed text-xs pt-0.5">
                          {variable.description}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Seção: Exemplos */}
              {Array.isArray(content.examples) && content.examples.length > 0 && (
                <section className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <BookOpen className="h-4 w-4 text-blue-500" />
                    {sections.examples}
                  </div>
                  <div className="space-y-3 pl-6">
                    {content.examples.map((example, index) => (
                      <div 
                        key={index}
                        className="p-3.5 rounded-lg bg-muted/50 border border-border/50 space-y-1.5"
                      >
                        <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                          <span className="flex-shrink-0 h-5 w-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
                            {index + 1}
                          </span>
                          {example.scenario}
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed pl-7">
                          {example.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Seção: Dica */}
              {content.tip && (
                <section className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <Lightbulb className="h-4 w-4 text-amber-500" />
                    {sections.tip}
                  </div>
                  <div className="pl-6">
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <p className="text-sm text-amber-700 dark:text-amber-300 leading-relaxed">
                        {content.tip}
                      </p>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </ScrollArea>

          {/* Footer com botão de confirmação */}
          <div className="px-6 py-4 border-t bg-muted/30">
            <Button 
              onClick={() => setIsOpen(false)}
              className="w-full"
              size="lg"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              {content.button}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default FeatureHelp;
